/**
 * `AppDialog` — le dialogue maison (SPEC-UI §7.1).
 *
 * **Jamais** `window.confirm` / `window.alert` / `window.prompt` : règle globale de Mathieu,
 * reprise dans CLAUDE.md. Tout ce qui demande une réponse au joueur passe ici — y compris la
 * saisie de texte (nommer une série), d'où la variante `prompt`.
 *
 * Comportement : overlay bloquant, `role="dialog"` + `aria-modal`, focus posé à l'ouverture et
 * **piégé** dans la boîte, Échap = annulation — sauf `danger: true`, où seul un bouton répond.
 */

import { useEffect, useRef, useState } from "react";

import { useUIStore } from "../stores/uiStore";
import { Fenetre } from "./Fenetre";
import { useSfx } from "./SoundProvider";

export function AppDialog() {
  const demande = useUIStore((s) => s.dialog);
  const fermer = useUIStore((s) => s.fermerDialogue);
  const boite = useRef<HTMLDivElement>(null);
  const premierBouton = useRef<HTMLButtonElement>(null);
  const champ = useRef<HTMLInputElement>(null);
  const [saisie, setSaisie] = useState("");
  const [erreur, setErreur] = useState<string | null>(null);
  const sfx = useSfx();

  useEffect(() => {
    if (!demande) return;
    sfx("dialog_open");
    if (demande.kind === "prompt") {
      setSaisie(demande.valeurInitiale ?? "");
      setErreur(null);
      champ.current?.focus();
    } else {
      premierBouton.current?.focus();
    }
  }, [demande, sfx]);

  useEffect(() => {
    if (!demande) return;

    const auClavier = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        const verrouille =
          (demande.kind === "confirm" && demande.danger === true) ||
          (demande.kind === "choose" && demande.annulable === false);
        if (verrouille) {
          e.preventDefault();
          return;
        }
        e.preventDefault();
        repondre(null);
        return;
      }
      if (e.key !== "Tab" || !boite.current) return;
      // Piège à focus : on ne sort pas de la boîte à la tabulation.
      const focusables = boite.current.querySelectorAll<HTMLElement>(
        "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])",
      );
      if (focusables.length === 0) return;
      const premier = focusables[0]!;
      const dernier = focusables[focusables.length - 1]!;
      if (e.shiftKey && document.activeElement === premier) {
        e.preventDefault();
        dernier.focus();
      } else if (!e.shiftKey && document.activeElement === dernier) {
        e.preventDefault();
        premier.focus();
      }
    };

    document.addEventListener("keydown", auClavier);
    return () => document.removeEventListener("keydown", auClavier);
  });

  if (!demande) return null;

  function repondre(valeur: string | boolean | null) {
    const d = demande;
    if (!d) return;
    sfx("dialog_close");
    fermer();
    if (d.kind === "choose" || d.kind === "prompt") {
      d.resolve(typeof valeur === "string" ? valeur : null);
    } else {
      d.resolve(valeur === true);
    }
  }

  /** Valider une saisie : on garde la main tant que la validation refuse. */
  function validerSaisie() {
    if (demande?.kind !== "prompt") return;
    const message = demande.valider?.(saisie) ?? null;
    if (message) {
      setErreur(message);
      return;
    }
    repondre(saisie);
  }

  // ⟦C48 · A-168 §3⟧ Le dialogue maison est une `Fenetre` (bandeau violet, corps tramé, bordure en
  // escalier) : il servait de carte crème à coins arrondis, la seule surface de décision du jeu
  // (palier franchi, ouverture d'un étage, « Effacer cette chaîne ? ») restée hors de la DA. Le
  // titre passe dans le bandeau ; SANS croix (`onFermer` absent) — `danger` verrouille Échap, et
  // seuls les boutons répondent : c'est ce qui avait tenu ce composant à l'écart de ⟦C44⟧. Les
  // boutons d'action vivent dans le pied, hors du défilement du corps.
  const actions =
    demande.kind === "choose" ? null : demande.kind === "prompt" ? (
      <>
        <button type="button" className="bouton discret" onClick={() => repondre(null)}>
          Annuler
        </button>
        <button type="button" className="bouton primaire" onClick={validerSaisie}>
          Valider
        </button>
      </>
    ) : (
      <>
        {demande.kind === "confirm" ? (
          <button type="button" className="bouton discret" onClick={() => repondre(false)}>
            {demande.cancelLabel ?? "Annuler"}
          </button>
        ) : null}
        <button
          type="button"
          ref={premierBouton}
          className={`bouton primaire${demande.kind === "confirm" && demande.danger ? " danger" : ""}`}
          onClick={() => repondre(true)}
        >
          {demande.kind === "confirm"
            ? (demande.confirmLabel ?? "Confirmer")
            : (demande.confirmLabel ?? "J'ai compris")}
        </button>
      </>
    );

  return (
    <div className="overlay" role="presentation">
      <div className="dialogue-fenetre" ref={boite}>
        <Fenetre
          titre={demande.title}
          icone={demande.kind === "info" ? demande.icone : undefined}
          role="dialog"
          ariaModal
          className="fenetre-dialogue"
          pied={actions}
        >
          {"body" in demande && demande.body ? <p className="dialogue-corps">{demande.body}</p> : null}

          {demande.kind === "choose" ? (
            <div className="dialogue-options">
              {demande.options.map((option, i) => (
                <button
                  key={option.id}
                  type="button"
                  ref={i === 0 ? premierBouton : undefined}
                  className={`bouton bouton-option${option.danger ? " danger" : ""}`}
                  onClick={() => repondre(option.id)}
                >
                  <span className="bouton-option-label">{option.label}</span>
                  {option.hint ? <span className="bouton-option-hint">{option.hint}</span> : null}
                </button>
              ))}
            </div>
          ) : demande.kind === "prompt" ? (
            <>
              <label className="champ">
                <span className="champ-label">{demande.label}</span>
                <input
                  ref={champ}
                  className="champ-saisie"
                  value={saisie}
                  placeholder={demande.placeholder}
                  onChange={(e) => {
                    setSaisie(e.target.value);
                    setErreur(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      validerSaisie();
                    }
                  }}
                />
              </label>
              {erreur ? <p className="erreur">{erreur}</p> : null}
            </>
          ) : null}
        </Fenetre>
      </div>
    </div>
  );
}
